import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DecisionQuestion from './DecisionQuestion';
import ProgressBar from './ProgressBar';
import ScenarioSelector from './ScenarioSelector';
import type { SelectorState } from '../../types/prioritization-selector';
import { scenarios, getQuestionById } from '../../data/prioritization-selector/decisionTree';

interface DecisionFlowProps {
  state: SelectorState;
  onUpdateResponse: (questionId: string, value: string | string[] | number) => void;
  onSelectScenario: (scenarioId: string) => void;
}

const DecisionFlow: React.FC<DecisionFlowProps> = ({
  state,
  onUpdateResponse,
  onSelectScenario
}) => {
  const navigate = useNavigate();
  const [showScenarioSelector, setShowScenarioSelector] = useState(!state.selectedScenario);

  useEffect(() => {
    if (state.isComplete && state.recommendation) {
      navigate('results');
    }
  }, [state.isComplete, state.recommendation, navigate]);

  const handleResponse = (questionId: string, value: string | string[] | number) => {
    onUpdateResponse(questionId, value);
  };

  const handleScenarioSelect = (scenarioId: string) => {
    onSelectScenario(scenarioId);
    setShowScenarioSelector(false);
  };

  const handleStartOver = () => {
    setShowScenarioSelector(true);
  };

  if (showScenarioSelector) {
    return (
      <ScenarioSelector
        scenarios={scenarios}
        onSelect={handleScenarioSelect}
        onStartOver={handleStartOver}
      />
    );
  }

  const currentQuestion = getQuestionById(state.currentQuestion || 'Q1');
  
  if (!currentQuestion) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-display font-bold text-[var(--text-pri)] mb-4">
          No question available
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          There was an error loading the next question.
        </p>
        <button
          onClick={handleStartOver}
          className="btn-primary"
        >
          Start Over
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <ProgressBar 
        current={state.path.length + 1} 
        total={8} // Q1–Q6 + Qa (time) + Qb (scoring preference)
        path={state.path}
      />
      
      <DecisionQuestion
        question={currentQuestion}
        value={state.responses.find(r => r.questionId === currentQuestion.id)?.value}
        onResponse={handleResponse}
        onStartOver={handleStartOver}
      />
    </div>
  );
};

export default DecisionFlow;

